import { useState, useEffect } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import api, { formatCurrency } from "../lib/api";
import { toast } from "sonner";
import {
  Landmark, ArrowRight, Sparkles,
  CircleDollarSign, Building2, FileCheck, Zap,
} from "lucide-react";

const HowToInvestPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const loadAll = async () => {
    try {
      const r1 = await api.get("/settings/investment-instructions");
      setData(r1.data);
    } catch {
      toast.error("Failed to load instructions");
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  if (!data) {
    return <DashboardLayout><div className="py-12 text-center text-slate-500">Loading instructions…</div></DashboardLayout>;
  }

  const { jv, flow_steps } = data;

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-5xl" data-testid="how-to-invest-page">
        {/* Hero */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="text-blue-600" size={16} />
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              How to invest in partners through EchoSense Capital Club
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-serif tracking-tight text-slate-900">
            Vetted partners looking for a match.
          </h1>
          <p className="text-slate-600 mt-3 text-base max-w-3xl">
            Every dollar is wired to <strong>FINBPM, the EchoSense Partner Account</strong>, held
            in trust until allocated, and allocated by FINBPM to its regulated brokerage partner.
            Nothing touches a personal account — ever.
          </p>

          <div className="flex gap-3 flex-wrap mt-5">
            <Badge className="bg-blue-50 text-blue-700 border-blue-200">
              <Landmark size={12} className="mr-1" /> Partner Account: {jv.entity_name}
            </Badge>
            <Badge className="bg-slate-100 text-slate-700 border-slate-200">
              <Building2 size={12} className="mr-1" /> Partner Platform
            </Badge>
          </div>
        </section>

        {/* Qualification-paths card (standard rewards vs 90-day trial) removed
            (STARSEED-CAPMARKETS-080) — compensation/trial surfaces retired per
            attorney direction. */}

        {/* Visual flow */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle>Funding flow</CardTitle>
            <CardDescription>How your capital moves — end-to-end transparency.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-stretch justify-between gap-3 flex-wrap" data-testid="flow-diagram">
              <FlowNode icon={CircleDollarSign} label="You" sub="USD wire" tone="slate" />
              <Arrow />
              <FlowNode icon={Landmark} label="Partner Account" sub={jv.entity_name} tone="blue" />
              <Arrow />
              <FlowNode icon={Building2} label="EchoSense Capital Club" sub="Portal + compliance" tone="blue" />
              <Arrow />
              <FlowNode icon={Zap} label="Broker Partner" sub="Qualified partners" tone="emerald" />
            </div>
            <p className="text-xs text-slate-500 mt-4">
              All withdrawals, deposits, and capital allocations are managed inside FINBPM's
              portal; positions and performance are reflected in your partner account portal on a
              daily basis.
            </p>
          </CardContent>
        </Card>

        {/* Step-by-step */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle>Step-by-step</CardTitle>
            <CardDescription>A five-step path from qualification to allocation.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {flow_steps.map((s) => (
              <div key={s.n} className="flex gap-4 p-4 rounded-lg border border-slate-200 bg-slate-50/50" data-testid={`step-${s.n}`}>
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 text-white font-semibold flex items-center justify-center shrink-0">
                  {s.n}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{s.title}</h3>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.detail}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Wire instructions — the legacy portal-hosted wire card is retired
            (STARSEED-CAPMARKETS-063): bank details are issued by FinBPM (SSB
            account) via the official email and mirrored on the FINBPM funding
            page only after the countersigned agreement. Displaying a second
            copy here risked stale/mismatched details — which is exactly what
            happened. */}
        <Card className="bg-white border-slate-200" data-testid="wire-instructions">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Landmark className="text-blue-600" size={20} /> Wire instructions — issued by FinBPM
            </CardTitle>
            <CardDescription>
              Your official wire instructions (SSB bank details and the exact reference lines your
              wire must carry) are issued directly by FinBPM after your participation agreement is
              countersigned. They arrive by email and are mirrored on your FINBPM funding page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Never wire to details from any other source — only the FinBPM-issued instructions.
              If details you were sent don't match this portal's FINBPM page, stop and contact the
              team before sending anything.
            </div>
            {(user?.role === "client" || user?.role === "referrer") ? (
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                onClick={() => window.location.href = "/partner-funding"}
                data-testid="wire-instructions-finbpm-cta"
              >
                <Landmark size={16} className="mr-1" /> View my FINBPM funding instructions
                <ArrowRight size={16} className="ml-1" />
              </Button>
            ) : (
              <p className="text-xs text-slate-500">
                Members see their FinBPM-issued instructions on the FINBPM funding page once their
                agreement is countersigned. Staff manage the underlying details in Settings and the
                Funding Queue.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
              <Stat label="Minimum first tranche" value={formatCurrency(jv.minimum_investment)} />
              <Stat label="Settlement window" value="1–2 business days" />
              <Stat label="Portal reflection" value="Near-real-time" />
            </div>
          </CardContent>
        </Card>

        {/* Paytiko status alert + Comp Fund card removed (STARSEED-CAPMARKETS-053;
            full backend Paytiko excision tracked by STARSEED-CAPMARKETS-057). */}

        {/* Next steps CTA */}
        <Card className="bg-gradient-to-br from-blue-600 to-emerald-500 border-0 text-white">
          <CardContent className="p-8 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-xl font-semibold">Ready to fund your allocation?</h3>
              <p className="text-white/85 text-sm mt-1">
                Follow your FinBPM-issued instructions exactly — including the reference lines —
                and the portal will do the rest. Need help? Use the "Need Help?" button
                bottom-right on any page.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" className="bg-white text-blue-700 hover:bg-white/90" onClick={() => window.location.href = "/kyc"} data-testid="cta-kyc">
                <FileCheck size={16} className="mr-1" /> KYC status
              </Button>
              <Button variant="secondary" className="bg-white text-blue-700 hover:bg-white/90" onClick={() => window.location.href = "/payments"} data-testid="cta-payments">
                <CircleDollarSign size={16} className="mr-1" /> Payments <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
};

// Small helpers

const FlowNode = ({ icon: Icon, label, sub, tone }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return (
    <div className={`flex-1 min-w-[160px] rounded-lg border ${tones[tone]} p-4 text-center`}>
      <Icon className="mx-auto mb-1" size={22} />
      <p className="text-sm font-semibold">{label}</p>
      <p className="text-xs opacity-70 mt-0.5">{sub}</p>
    </div>
  );
};

const Arrow = () => (
  <div className="flex items-center text-slate-400">
    <ArrowRight size={18} />
  </div>
);

const Stat = ({ label, value }) => (
  <div className="p-3 rounded-md border border-slate-200 bg-white">
    <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-semibold text-slate-900 mt-0.5">{value}</p>
  </div>
);

export default HowToInvestPage;
